import { STORIES } from 'routes/routeConstant'
import { useLessonDiscussionsEnabled } from 'components/discussion/useLessonDiscussionsEnabled'
import { discussionPath } from 'components/discussion/discussionLogic'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { AppUseSelector, AppUseDispatch } from '../../redux/hooks'
import { v4 as uuid } from 'uuid'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { formatRelative } from '../../i18n/format'
import { useChineseLang } from '../../i18n/chinese'
import { correctionValidate, normalizeTeacherNotes, splitTeacherNoteText } from 'utils/teacherCorrection'
import { getStory, getWordDetail, wordDoesNotKnow } from '../../redux/actions/storyAction'
import Sidebar from './Sidebar'
import PLayer from 'containers/player/Player'
import Recorder from 'containers/recorder/Recorder'
import { getSignedUrl, uploadFileOnS3 } from 'utils/S3Upload'
import { AddWordToHackChinese, handleAddWordToSkritter, logout } from '../../redux/actions/userAction'
import Diff from 'containers/Diff'
import { setCorrections, setCurrentCorrection, submitCorrection } from '../../redux/actions/dashboardAction'
import Loader from 'containers/loader/loader'
import { getQuestion } from '../../redux/actions/questionAction'
import { setAudioRecording } from '../../redux/actions/recorderAction'
import { useNavigate } from 'react-router-dom'
import OneLineContent from 'containers/RenderStroyComponent/OneLineContent'
import { converterWord, getWordLine, getWordTypeFromStory, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import { CHECK_WORD_IS_CLICKED } from '../../redux/types'
import { levelLabelBand, levelLabelFromEnum, levelLabelFromWordCount, localizedLevelLabel } from '../story-card/constants'
import { getStoryAudio } from 'utils/storyAudio'
import { splitParagraphs } from '../write-speak/logic'
import {
  AnswerWithWrittenWords,
  hasLearnerRecording,
  hasWritingSpeakingData,
  readPassageIndex,
  SpeakingModeChip,
  WordsToTryStrip,
  writtenAnswer,
} from './WritingSpeaking'
// .cx-note: the preview shows teacher notes the way the learner's correction page does
import '../Corrections/corrections.css'

/** '3 days ago' for a correction's createdAt (epoch milliseconds, as a number or a string). */
export const submittedAgo = (createdAt: unknown): string => formatRelative(parseInt(String(createdAt), 10))

const Dashboard: React.FC = (props: any) => {
  const {
    credentials,
    currentStoryCorrection,
    questionDetail,
    story,
    currentAudioRecording,
    user_auth_token,
    skritter_token,
  } = props
  const { memberId } = credentials
  const { t } = useTranslation('dashboard')
  const zhLang = useChineseLang()
  const navigate = useNavigate()
  const dispatch = AppUseDispatch()
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }) => dashboard)
  const characterPreference = credentials?.customFields?.character
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStoryAvailable, setIsStoryAvailable] = useState(false)
  const [showStoryLine, setShowStoryLine] = useState(true)
  const [audioRecordingUrl, setAudioRecordingUrl] = useState('')
  const [correctionRecordingUrl, setCorrectionRecordingUrl] = useState('')
  const [answer, setAnswer] = useState('')
  const [recordingError, setRecordingError] = useState('')
  const [answerError, setAnswerError] = useState({ error: '' })
  const [firstLineStory, setFirstLineStory] = useState<any>({})
  const showWritingSpeaking = hasWritingSpeakingData(currentStoryCorrection?.data)
  // Teachers may join a lesson's discussion when they want to; this is their way in.
  const discussionsOn = useLessonDiscussionsEnabled()
  const discussionStoryId = currentStoryCorrection?.data?.storyId
  const passageIndex = readPassageIndex(currentStoryCorrection?.data)
  // Each half of the correction is required only when the learner sent that half:
  // a voice-only submission needs no written correction, a written-only one no recording.
  const learnerAnswer = writtenAnswer(currentStoryCorrection?.data)
  const learnerRecorded = hasLearnerRecording(currentStoryCorrection?.data)
  // The learner's recording element. Player plays through the shared getStoryAudio
  // element, and this memo runs in the Dashboard render before the answer Player
  // mounts or changes URL, so both resolve to the same element and the Words to
  // try strip can seek it. Legacy docs skip this entirely.
  const studentAudio = useMemo(
    () => (showWritingSpeaking && audioRecordingUrl ? getStoryAudio(audioRecordingUrl) : null),
    [showWritingSpeaking, audioRecordingUrl],
  )
  const [activeIndex, setActiveIndex] = useState('-1')
  const { wordDetails, isWordClick } = AppUseSelector(({ story }) => story)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const storyDifficultyLabel = useMemo(() => {
    // Prefer the backend's stored combined level (story_level); fall back to
    // deriving from unique_word_count, then to the coarse Prismic level.
    const fromStoryLevel = levelLabelFromEnum(
      story?.story_level ?? currentStoryCorrection?.data?.story_level
    )
    if (fromStoryLevel) return fromStoryLevel

    const uniqueWordCount = Number(story?.unique_word_count ?? story?.uniqueWordCount)
    const fromWordCount = levelLabelFromWordCount(uniqueWordCount)
    if (fromWordCount) return fromWordCount

    return localizedLevelLabel(null, currentStoryCorrection?.data?.level) || ''
  }, [story, currentStoryCorrection?.data?.story_level, currentStoryCorrection?.data?.level])
  const storyDifficultyBand = levelLabelBand(storyDifficultyLabel)
  const storyDifficultyBadgeClass = storyDifficultyBand === 'advanced'
    ? 'm-b-error color-white text-capitalize'
    : storyDifficultyBand === 'intermediate'
      ? 'm-b-primary-2 color-white text-capitalize'
      : 'm-b-primary-1 color-white text-capitalize'

  const handleClickOutside = (event) => {
    const isInsideSpecificButtons = event.target.closest('[data-specific-buttons]')
    if (
      !isInsideSpecificButtons &&
      !contentWordRef.current?.contains(event.target) &&
      !extraDivRef.current?.contains(event.target)
    ) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, firstLineStory, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId: firstLineStory?.storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'correction',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
      setWordDetailVisible(false)
      setImportanceOpen(false)
      setMasteryOpen(false)
      setActiveIndex('-1')
    } else if (isInsideSpecificButtons) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, firstLineStory, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId: firstLineStory?.storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'correction',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
    }
  }

  useEffect(() => {
    if (openExampleModal) {
      return
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [extraDivRef, contentWordRef, openExampleModal, wordDetailVisible, isWordClick, wordDetails])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const onLogout = () => {
    logout(true)
  }

  const onMoveToIntro = () => {
    navigate(STORIES)
  }

  // Shown the way the learner will see it: notes split off below the marked-up text.
  const preview = splitTeacherNoteText(currentStoryCorrection?.data?.correctedAnswer || answer)

  const onTextChange = (e) => {
    setAnswer(e.target.value)
  }

  useEffect(() => {
    setFirstLineStory({})
    let firstLineWords: any[] = []
    // Read a passage: show the paragraph the learner read, split the way the learner's step split it.
    const paragraphs = passageIndex !== null ? splitParagraphs<any>(story?.storyContent) : []
    if (paragraphs.length) {
      firstLineWords = paragraphs[Math.min(passageIndex as number, paragraphs.length - 1)].map((wordObj) => ({
        ...wordObj,
      }))
    } else {
      let firstLineBreak = false

      story?.storyContent?.forEach((wordObj) => {
        firstLineBreak =
          !firstLineBreak && (wordObj.word.includes('</p>') || wordObj.word.includes('</p><p>'))
            ? true
            : firstLineBreak

        if (!firstLineBreak) firstLineWords.push({ ...wordObj })

        return wordObj
      })
    }
    setFirstLineStory({
      storyId: story?.id,
      arContent: firstLineWords,
      storyContent: firstLineWords,
      newWord1: story.newWord1,
      newWord2: story.newWord2,
      oldWords: story.oldWords,
      showPinYin: false,
      newWordsArray: story.newWordsArray,
      learningWords: story.learningWords,
      growingWords: story.growingWords,
      developingWords: story.developingWords,
      masteredWords: story.masteredWords,
    })
  }, [story, passageIndex])

  useEffect(() => {
    if (currentAudioRecording.size && currentAudioRecording.size !== undefined) setRecordingError('')
  }, [currentAudioRecording.size])

  useEffect(() => {
    if (currentStoryCorrection?.data?.storyId && currentStoryCorrection?.data?.level)
      Promise.all([
        getStory(currentStoryCorrection?.data?.storyId, currentStoryCorrection?.data?.level),
        getQuestion(currentStoryCorrection?.data?.storyId, currentStoryCorrection?.data?.level),
      ]).then(() => setIsStoryAvailable(true))
  }, [currentStoryCorrection?.data, memberId])

  useEffect(() => {
    setAudioRecording({})
    setIsStoryAvailable(false)
    setAudioRecordingUrl('')
    setCorrectionRecordingUrl('')
    setAnswer('')
    setAnswerError({ error: '' })
    setRecordingError('')
    if (currentStoryCorrection?.data?.recordingUrl && currentStoryCorrection?.data?.recordingUrl !== '') {
      getSignedUrl({ key: currentStoryCorrection?.data?.recordingUrl })
        .then((res) => {
          setAudioRecordingUrl(res)
        })
        .catch(() => setAudioRecordingUrl(''))
    }
    if (
      currentStoryCorrection?.data?.correctedRecordingUrl &&
      currentStoryCorrection?.data?.correctedRecordingUrl !== ''
    ) {
      getSignedUrl({ key: currentStoryCorrection?.data?.correctedRecordingUrl })
        .then((res) => {
          setCorrectionRecordingUrl(res)
        })
        .catch(() => setCorrectionRecordingUrl(''))
    }
  }, [currentStoryCorrection?.data])

  const onSubmitAnswer = async () => {
    let validationFailed = 0
    if (learnerRecorded && (props.currentAudioRecording.size === undefined || !props.currentAudioRecording.size)) {
      validationFailed++
      setRecordingError(t('teacher.recordingRequired'))
    }

    if (learnerAnswer) {
      const answerHasError = await correctionValidate(answer)
      validationFailed = answerHasError.error !== '' ? validationFailed + 1 : validationFailed
      setAnswerError(answerHasError)
    }

    if (validationFailed > 0) return

    if (!isSubmitting) {
      setIsSubmitting(true)

      try {
        let uploadResponse
        if (props.currentAudioRecording.size) {
          const uploadKey = `${memberId}/${currentStoryCorrection?.data.storyId}-${uuid()}`
          const payload = {
            uploadKey,
            file: props.currentAudioRecording,
            contentType: props.currentAudioRecording?.type,
          }
          uploadResponse = await uploadFileOnS3(payload)
        }
        submitCorrection(
          currentStoryCorrection?.data.storyId,
          normalizeTeacherNotes(answer),
          currentStoryCorrection?.data.memberId,
          uploadResponse !== undefined ? uploadResponse?.Key : '',
        )
          .then((res) => {
            setIsSubmitting(false)
            setCurrentCorrection({ ...res, ref: currentStoryCorrection.ref })
            props.corrections.forEach((correction) => {
              // if (correction.ref === currentStoryCorrection.ref) correction.data.isOpen = false
              if (
                `${correction.data.memberId} + ${correction.data.storyId}` ===
                `${currentStoryCorrection.data.memberId} + ${currentStoryCorrection?.data?.storyId}`
              )
                correction.data.isOpen = false

              return correction
            })
            setCorrections([...props.corrections])
          })
          .catch(() => setIsSubmitting(false))
      } catch (e) {
        setIsSubmitting(false)
      }
    }
  }

  const handle = {
    onClickWord: (event, word, sentence, key) => {
      if (isMobile) {
        const targetElement = document.getElementById(key)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' })
        }
      }

      handle.translateWord(event, sentence, word)

      setActiveIndex(activeIndex === key ? `-1` : key)
      event.preventDefault()
      event.stopPropagation()
    },
    translateWord: (event: any, sentence: any, wordInfo: any) => {
      if (
        (event.type === 'mouseover' || event.type === 'click') &&
        event.target.innerText &&
        event.target.innerText.length > 0
      ) {
        const word = event.target.innerText
        const allData = [...firstLineStory?.arContent, ,]

        if (/^[&.-”“，、；？@!#$%^&*()]+$/.test(word) || word.trim() === '。') {
          return
        }
        const wordTranslate = allData?.filter((iword) => converterWord(iword?.word, characterPreference) === word)
        if (wordTranslate && wordTranslate.length > 0) {
          handle.getWordDetail(word, sentence, wordTranslate[0])
        } else {
          handle.getWordDetail(word, sentence, wordInfo)
        }
      }
    },
    getWordDetail: async (word: string, sentence: any, data: any) => {
      const sentenceString = getWordLine(sentence)
      const basicDetail = {
        word,
        pinyin: data?.pinyin,
        english: data?.english,
        sentenceString,
        sentenceWords: getSentenceWords(sentence),
      }

      if (word && memberId) {
        setWordDetailVisible(true)
        const mastery = getWordTypeFromStory(word, firstLineStory, characterPreference)
        await getWordDetail({
          detail: basicDetail,
          payload: { word: wordSimplified(word), memberId },
          wordType: mastery,
        }).then((res) => {
          handle.onWordClickIntegration({ word: res?.word, pinyin: res?.pinyin, english: res?.english })
        })
      }
    },
    onWordClickIntegration: (obj: any) => {
      if (user_auth_token) {
        AddWordToHackChinese({
          word: obj?.word,
          pinyin: obj?.pinyin,
          user_auth_token: user_auth_token,
        })
      }
      if (skritter_token) {
        handleAddWordToSkritter({
          word: obj?.word,
          translate: { pinyin: obj?.pinyin, english: obj?.english },
          title: firstLineStory?.storyTitleContent,
          skritter_token: skritter_token,
        })
      }
    },
  }

  return (
    <>
      <div className='dashboard-main-div w-d-grid'>
        <Sidebar />

        <div className='right-side mb-30 mt-30'>
          <div className='section-2'>
            <div className='section-story'>
              <div className='w-row border-bottom w-d-flex'>
                <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-6  mt-10 mb-10 content-left'>
                  <span className='introduction-title'>{t('teacher.submittedAnswer')}</span>
                </div>
                <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-6 content-right'>
                  <button onClick={onMoveToIntro} className='button-2 bg-gray-4 login w-button mr-10'>
                    {t('teacher.story')}
                  </button>
                  <button onClick={onLogout} className='button-2 bg-gray-4 login w-button'>
                    {t('teacher.logOut')}
                  </button>
                </div>
                {/* <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-6  pinyin-button pr-10'>
                  <button className='button-2 bg-light-gray w-button'>jkl</button>
                </div> */}
              </div>

              {currentStoryCorrection && isStoryAvailable ? (
                <>
                  <div className='w-nav story-nav-content'>
                    <span className='story-nav-pre-heading'>
                      {t('teacher.submitted', { time: submittedAgo(currentStoryCorrection?.data?.createdAt) })}
                    </span>
                    <div className='story-nav-user-introduction'>
                      <h3 className='story-nav-content-title'>
                        {currentStoryCorrection?.data.memberName}
                        <span
                          className={storyDifficultyBadgeClass}
                        >
                          {storyDifficultyLabel}
                        </span>
                      </h3>
                    </div>

                    <span className='story-nav-id'> {t('teacher.memberId', { id: currentStoryCorrection?.data.memberId })} </span>
                  </div>
                  <div className='w-row  w-d-flex section-story-content mb-20'>
                    <div className='w-col-12 w-col-medium-12 w-col-small-12 w-col-tiny-12 content-left box-padding'>
                      <div className='answer-box box-border'>
                        <span className='box-title '>
                          {passageIndex !== null
                            ? ` ${t('teacher.passageTitle', { number: String(passageIndex + 1) })} `
                            : ` ${t('teacher.story')} `}
                        </span>
                        <span className='up-arrow' onClick={() => setShowStoryLine(!showStoryLine)}>
                          <i className={showStoryLine ? 'fa fa-chevron-up' : 'fa fa-chevron-down'}></i>
                        </span>
                        {showStoryLine ? (
                          <>
                            <hr className='story-bottom-border'></hr>
                            <div className='story-content-padding border-top'>
                              <div className='word_wrapper'>
                                {firstLineStory?.arContent &&
                                  firstLineStory?.arContent.length &&
                                  firstLineStory?.storyContent?.map((wordObj, index) => (
                                    <OneLineContent
                                      key={index}
                                      iterator={'STORY-FIRST-LINE-DASHBOARD'}
                                      wordObj={wordObj}
                                      WIndex={index}
                                      contentDetail={firstLineStory?.storyContent || []}
                                      story={firstLineStory}
                                      showPinYin={true}
                                      activeIndex={activeIndex}
                                      handleClickWord={handle.onClickWord}
                                    />
                                  ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='w-row  w-d-flex section-story-content'>
                    <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-6 content-left box-padding border-right'>
                      <div className='answer-box box-border'>
                        <div className='copy-title-div'>
                          {questionDetail?.question ? <h4>{questionDetail?.question}</h4> : ''}
                          {discussionsOn && discussionStoryId ? (
                            <a
                              className='dash-discussion-link'
                              href={discussionPath(discussionStoryId)}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {t('teacher.openDiscussion')}
                            </a>
                          ) : null}
                          {learnerAnswer ? (
                            <CopyToClipboard text={learnerAnswer}>
                              <button className='copy-icon'>
                                <i className='fa fa-clone'></i>
                              </button>
                            </CopyToClipboard>
                          ) : (
                            ''
                          )}
                        </div>
                        {showWritingSpeaking ? (
                          <>
                            <div className='dash-ws-answer-head'>
                              <span className='box-title'> {t('teacher.theAnswer')} </span>
                              <SpeakingModeChip data={currentStoryCorrection?.data} audio={studentAudio} />
                            </div>
                            <WordsToTryStrip data={currentStoryCorrection?.data} audio={studentAudio} />
                          </>
                        ) : (
                          <span className='box-title'> {t('teacher.theAnswer')} </span>
                        )}

                        <div className='story-content story-words'>
                          {learnerAnswer ? (
                            <AnswerWithWrittenWords
                              answer={learnerAnswer}
                              wordsWritten={currentStoryCorrection?.data?.wordsWritten}
                            />
                          ) : learnerRecorded ? (
                            t('teacher.voiceOnly')
                          ) : (
                            t('teacher.noAnswer')
                          )}
                        </div>
                        {audioRecordingUrl && audioRecordingUrl !== '' ? (
                          <div className='w-row w-col-11 w-col-medium-11 w-col-small-12 w-col-tiny-12 box-audio'>
                            <PLayer audio={audioRecordingUrl}></PLayer>
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                    <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-6 content-left'>
                      <div className='correction-box'>
                        <div className='correction-box-content box-padding'>
                          <div className='box-title mb-10'> {t('teacher.corrections')} </div>
                          <div className='correction-words'>
                            {/* Add text area*/}
                            {!currentStoryCorrection?.data?.isOpen ? (
                              <div className='story-content story-words'>
                                {currentStoryCorrection?.data?.correctedAnswer
                                  ? currentStoryCorrection?.data?.correctedAnswer
                                  : t('teacher.noCorrection')}
                              </div>
                            ) : !learnerAnswer ? (
                              <div className='story-content story-words'>
                                {learnerRecorded
                                  ? t('teacher.noWritingRecord')
                                  : t('teacher.noWriting')}
                              </div>
                            ) : (
                              <>
                                <textarea
                                  rows={4}
                                  placeholder={t('teacher.answerPlaceholder')}
                                  value={answer}
                                  onChange={onTextChange}
                                  className={answerError?.error !== '' ? 'input-error' : ''}
                                />
                                {answerError?.error !== '' && (
                                  <div className='error'>
                                    <i className='fa fa-exclamation-circle' /> {answerError?.error}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className='preview-box-contenr box-padding border-top'>
                          {learnerAnswer ? (
                            <>
                              <span className='box-title'> {t('teacher.preview')} </span>
                              <div className='story-content preview-words'>
                                {preview.body.length || preview.notes.length ? (
                                  <>
                                    <Diff
                                      string1={learnerAnswer}
                                      string2={preview.body}
                                      mode='characters'
                                      chineseWordFontSize={chineseWordFontSize}
                                    />
                                    {preview.notes.map((note, index) => (
                                      <p key={index} className='cx-note' lang={zhLang}>
                                        {note}
                                      </p>
                                    ))}
                                  </>
                                ) : (
                                  ''
                                )}
                              </div>
                            </>
                          ) : (
                            ''
                          )}
                          <div className='w-row w-col-12 w-col-medium-11 w-col-small-12 w-col-tiny-12 box-audio'>
                            {!currentStoryCorrection?.data?.isOpen && correctionRecordingUrl !== '' ? (
                              <PLayer audio={correctionRecordingUrl}></PLayer>
                            ) : audioRecordingUrl && audioRecordingUrl !== '' ? (
                              <>
                                <Recorder />
                                {recordingError !== '' && (
                                  <div className='error'>
                                    <i className='fa fa-exclamation-circle' /> {recordingError}
                                  </div>
                                )}
                              </>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='correction-button mt-30'>
                    {currentStoryCorrection?.data?.isOpen && (
                      <button
                        className='button-2 w-button green-btn-class'
                        disabled={isSubmitting}
                        onClick={onSubmitAnswer}
                      >
                        {isSubmitting ? <Loader showText={false} /> : t('teacher.submitCorrection')}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <Loader />
              )}
            </div>
          </div>
        </div>
      </div>
      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}

const mapStateToProps = (state: any) => ({
  user_auth_token: state.user.user_auth_token,
  skritter_token: state.user.skritter_token,
  currentStoryCorrection: state.dashboard.currentStoryCorrection,
  currentAudioRecording: state.recorder.currentAudioRecording,
  membership: state.user.membership,
  credentials: state.user.credentials,
  errors: state.UI.errors,
  story: state.story.currentStory,
  questionDetail: state.question,
  corrections: state.dashboard.corrections,
})

export default connect(mapStateToProps)(Dashboard)
